import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  MapPin,
  Wrench,
  ClipboardCheck,
  AlertOctagon,
  Package,
  FileText,
  Download,
  Upload,
  Droplets,
  LogOut,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useRef } from "react";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/locations", label: "Locations", icon: MapPin, testid: "nav-locations" },
  { to: "/equipment", label: "Equipment", icon: Wrench, testid: "nav-equipment" },
  { to: "/pm", label: "PM Checklist", icon: ClipboardCheck, testid: "nav-pm" },
  { to: "/faults", label: "Breakdowns", icon: AlertOctagon, testid: "nav-faults" },
  { to: "/parts", label: "Parts", icon: Package, testid: "nav-parts" },
  { to: "/reports", label: "Service Reports", icon: FileText, testid: "nav-reports" },
];

export default function Layout() {
  const fileRef = useRef(null);
  const { user, logout } = useAuth();

  const doExport = async () => {
    try {
      const { data } = await api.get("/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `carwash-maintenance-data-${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const onImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm("Replace all existing data with imported file?")) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      await api.post("/import", { ...parsed, replace: true });
      toast.success("Data imported. Refreshing...");
      setTimeout(() => window.location.reload(), 800);
    } catch {
      toast.error("Import failed. Check file format.");
    }
    e.target.value = "";
  };

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
  };

  return (
    <div className="flex min-h-screen bg-[#0A0A0A] text-white grain">
      <aside
        data-testid="sidebar"
        className="w-64 shrink-0 border-r border-white/10 bg-[#0A0A0A] flex flex-col relative z-10 sticky top-0 h-screen"
      >
        <div className="px-6 py-6 border-b border-white/10 flex items-center gap-3">
          <div className="w-9 h-9 bg-[#007AFF] flex items-center justify-center">
            <Droplets className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-black text-sm tracking-tighter uppercase leading-tight">
              Car Wash <span className="text-[#007AFF]">PM</span>
            </div>
            <div className="font-mono-tab text-[10px] tracking-[0.2em] text-[#737373] uppercase mt-0.5">
              Maint. Control
            </div>
          </div>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-2.5 border-l-2 transition-all duration-150 ease-out ${
                    isActive
                      ? "border-[#007AFF] bg-[#007AFF]/10 text-white"
                      : "border-transparent text-[#A3A3A3] hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={2} />
                <span className="text-sm font-medium tracking-tight">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <button
            data-testid="export-btn"
            onClick={doExport}
            className="w-full flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-white/30 hover:bg-white/5 text-xs text-[#E0E0E0] transition-all"
          >
            <Download className="w-3.5 h-3.5" /> Export Data
          </button>
          <button
            data-testid="import-btn"
            onClick={() => fileRef.current?.click()}
            className="w-full flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-white/30 hover:bg-white/5 text-xs text-[#E0E0E0] transition-all"
          >
            <Upload className="w-3.5 h-3.5" /> Import Data
          </button>
          <input ref={fileRef} type="file" accept="application/json" onChange={onImport} className="hidden" />
        </div>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-[#007AFF]/20 border border-[#007AFF]/40 flex items-center justify-center font-mono-tab text-xs text-[#007AFF]">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white truncate">{user?.name || user?.email}</div>
              <div className="font-mono-tab text-[9px] tracking-[0.2em] uppercase text-[#737373]">
                {user?.role || "user"}
              </div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-[#FF3B30]/40 hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] text-xs text-[#E0E0E0] transition-all"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden relative z-10">
        <Outlet />
      </main>
    </div>
  );
}
